import { useState, FormEvent } from "react";
import Toast from "@/components/base/Toast";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { booker, site } from "@/content";
import { Rich } from "@/lib/rich";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    type: "success" as "success" | "error",
    visible: false,
  });
  const phoneMask = usePhoneMask();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      setToast({ message: booker.contact.form.errors.tooLong, type: "error", visible: true });
      return;
    }
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    data.append("page_path", window.location.pathname);
    data.append("form_name", "contact-form");
    try {
      await fetch(site.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setToast({ message: booker.contact.form.errors.success, type: "success", visible: true });
      form.reset();
    } catch {
      setToast({ message: booker.contact.form.errors.generic, type: "error", visible: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gray-950">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-yellow-400/30 blur-[130px]" />
        <div className="absolute top-[20%] right-[-5%] w-[500px] h-[500px] rounded-full bg-red-500/25 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[30%] w-[500px] h-[500px] rounded-full bg-violet-500/30 blur-[130px]" />
        <div className="absolute bottom-[10%] right-[20%] w-[350px] h-[350px] rounded-full bg-cyan-400/25 blur-[100px]" />
        <div className="absolute top-[40%] left-[10%] w-[400px] h-[400px] rounded-full bg-pink-500/20 blur-[110px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/70 text-xs font-semibold tracking-widest uppercase mb-6 backdrop-blur-sm border border-white/10">{booker.contact.badge}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6">
              <Rich text={booker.contact.title} accentClassName="bg-gradient-to-r from-yellow-300 via-red-400 to-pink-400 bg-clip-text text-transparent" />
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">{booker.contact.text}</p>
            <div className="flex flex-col gap-4 text-white/70 text-sm">
              {booker.contact.info.map((item) => (
                <div key={item.text} className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br ${item.gradient}`}>
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                  {item.href ? (
                    <a href={item.href} className="hover:text-white transition-colors cursor-pointer">{item.text}</a>
                  ) : (
                    <span>{item.text}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Socials */}
            <div className="flex gap-3 mt-6 justify-center lg:justify-start">
              {site.socials.map((s) => (
                <a key={s.href} href={s.href} className={`w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br ${s.gradient ?? 'from-yellow-400 to-red-400'} text-white hover:opacity-80 transition-opacity cursor-pointer`}>
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-10">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {booker.contact.form.fields.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={booker.contact.form.fields.namePlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {booker.contact.form.fields.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={booker.contact.form.fields.emailPlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {booker.contact.form.fields.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder={booker.contact.form.fields.phonePlaceholder}
                    {...phoneMask}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {booker.contact.form.fields.company}
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder={booker.contact.form.fields.companyPlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {booker.contact.form.fields.message}
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder={booker.contact.form.fields.messagePlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/40 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 text-white font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60 shadow-lg"
                >
                  {loading ? booker.contact.form.submittingLabel : booker.contact.form.submitLabel}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </section>
  );
}